/**
 * InfoBox Component
 *
 * Consistent info/tip message box with icon
 * Text is black for better readability
 */

import { ReactNode } from 'react';
import { Icon } from './Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

interface InfoBoxProps {
  children: ReactNode;
  className?: string;
  variant?: 'info' | 'tip' | 'warning';
  size?: 'sm' | 'md';
}

export const InfoBox = ({
  children,
  className,
  variant = 'info',
  size = 'sm',
}: InfoBoxProps) => {
  const variantStyles = {
    info: {
      bg: 'bg-soft-mint border-light-teal-gray',
      icon: ICONS.INFO,
      iconColor: 'text-gray-900',
    },
    tip: {
      bg: 'bg-amber-50 border-amber-200',
      icon: ICONS.BULB,
      iconColor: 'text-gray-900',
    },
    warning: {
      bg: 'bg-red-50 border-red-200',
      icon: ICONS.WARNING,
      iconColor: 'text-red-600',
    },
  };

  const sizeStyles = {
    sm: 'text-xs',
    md: 'text-sm',
  };

  const styles = variantStyles[variant];

  return (
    <div className={cn('border rounded-lg p-3', styles.bg, className)}>
      <div className="flex gap-2">
        <Icon
          name={styles.icon}
          size="sm"
          className={cn(styles.iconColor, 'flex-shrink-0 mt-0.5')}
          ariaHidden
        />
        <div className={cn('text-gray-900', sizeStyles[size])}>
          {children}
        </div>
      </div>
    </div>
  );
};
